import { getWorkbookConditionContextSensitiveData } from 'cadenza/api-client/workbook-api';
import { Command } from 'cadenza/api-client/workbook-commands';
import { type RootStateOr, getStore } from 'cadenza/store/cadenza-store';
import type { WorkbookAttributeDto } from 'cadenza/workbook/api/workbook-attribute-dto';
import type { WorksheetId } from 'cadenza/workbook/api/worksheet-id';
import {
  formatDefaultConditionValuesAsText,
  formatSpatialConditionValueAsText,
  getDeactivated,
  getDeactivatedForWorksheets,
  hasValuesSet,
} from 'cadenza/workbook/conditions/condition-util';
import type { ConditionDto, WorkbookConditionValuesDto } from 'cadenza/workbook/model/condition-dto';
import type { ConditionId } from 'cadenza/workbook/model/condition-id.gen';
import { ConditionSettingsKey } from 'cadenza/workbook/model/condition-settings-key.gen';
import type { WorkbookDataSourceDto } from 'cadenza/workbook/model/workbook-data-source-dto';
import { formatQlConditionValuesAsText } from 'cadenza/workbook/ql-condition/ql-condition-workbook-utils';
import { executeCommand } from 'cadenza/workbook/workbook-core-reducers.state';
import {
  getActiveViewId,
  getActiveWorksheetId,
  getAttributeById,
  getConditionById,
  getConditionDeactivated,
  getDataSourceByObjecttypeId,
  getViewIdsAffectedByCondition,
  getWorkbookNameAndHash,
  getWorksheets,
  isConditionDeactivatedForActiveWorksheet,
  isConditionHidden,
  isConditionMandatory,
} from 'cadenza/workbook/workbook-core-selectors.state';
import type { WorkbookState } from 'cadenza/workbook/workbook.state';

/**
 * Retrieves data source for a given condition.
 * To be used by dynamic import in cadenza web classic.
 * @param conditionId
 */
export function getDataSourceForCondition(conditionId: ConditionId): WorkbookDataSourceDto {
  const condition = getConditionById(getStore().getState(), conditionId);
  const attribute = condition && getAttributeById(getStore().getState(), condition.attributeIds[0]);
  return attribute && getDataSourceByObjecttypeId(getStore().getState(), attribute.objecttypeId);
}

/**
 * Retrieves first condition attribute for a given condition.
 * To be used by dynamic import in cadenza web classic.
 * @param conditionId
 */
export function getConditionAttributeForCondition(conditionId: ConditionId): WorkbookAttributeDto {
  const condition = getConditionById(getStore().getState(), conditionId);
  return getAttributeById(getStore().getState(), condition.attributeIds[0]);
}

/**
 * Retrieves condition context sensitive data for current workbook
 * To be used by dynamic import in cadenza web classic.
 * @param conditionId
 */
export function getCurrentWorkbookConditionContextSensitiveData(conditionId: ConditionId) {
  const workbookNameAndHash = getWorkbookNameAndHash(getStore().getState());
  const worksheetId = getActiveWorksheetId(getStore().getState());
  return getWorkbookConditionContextSensitiveData(workbookNameAndHash, worksheetId, conditionId);
}

/**
 * Retrieves active viewId.
 * To be used by dynamic import in cadenza web classic.
 */
export function getWorkbookActiveViewId() {
  return getActiveViewId(getStore().getState());
}

export function hasAffectedViews(conditionId: ConditionId) {
  return getViewIdsAffectedByCondition(getStore().getState(), conditionId).length > 0;
}

/**
 * A condition is considered 'active', if it does have values set and affects
 * any data views.
 * <p>
 *    <b>Important note</b></br>
 *    This has <b>nothing</b> to do with the "deactivated" setting in the function
 *    {@link setConditionDeactivated}. While deactivating a condition by setting
 *    {@link ConditionSettingsKey#CONDITION_DEACTIVATED} to <code>true</code> will render
 *    it not active in the sense of this function as well, it is important to understand
 *    the difference.
 * </p>
 * @param conditionId - condition to check
 */
export function isActiveCondition(conditionId: ConditionId) {
  const condition = getConditionById(getStore().getState(), conditionId);
  return hasValuesSet(condition) && hasAffectedViews(conditionId);
}

/**
 * Activates or deactivates the given conditions.
 * If required, mandatory conditions should be filtered out before calling this function.
 *
 * <p>
 *     <b>Important note</b></br>
 *     This has <b>nothing</b> to do with the "active" status calculated in the
 *     function {@link isActiveCondition} above. This function alters the condition
 *     setting {@link ConditionSettingsKey#CONDITION_DEACTIVATED}. When the setting
 *     is <code>true</code>, this will mean the condition is also not active in
 *     that sense, but it is important to understand the difference.
 * </p>
 * @param conditionIds - List of conditions to alter
 * @param deactivated - <code>true</code> activates the conditions, <code>false</code> deactivates them.
 */
export function setConditionDeactivated(conditionIds: ConditionId[], deactivated: boolean) {
  const commands = conditionIds.map((conditionId) => deactivateConditionCommand(conditionId, deactivated));
  const clearDeactivationByWorksheetCommands = conditionIds.map((conditionId) =>
    getDeactivateForWorksheetsCommand(conditionId, []),
  );
  return getStore().dispatch(
    executeCommand(Command.COMPOSITE_COMMAND({ commands: [...commands, ...clearDeactivationByWorksheetCommands] })),
  );
}

/**
 * Activates or deactivates the given conditions for the current
 * worksheet. Mandatory conditions can not be deactivated, the backend
 * will see to that.
 *
 * <p>
 *     <b>Important note</b></br>
 *     This has <b>nothing</b> to do with the "active" status calculated in the
 *     function {@link isActiveCondition} above. This function alters the condition
 *     setting {@link ConditionSettingsKey#CONDITION_DEACTIVATED}. When the setting
 *     is <code>true</code>, this will mean the condition is also not active in
 *     that sense, but it is important to understand the difference.
 * </p>
 * @param conditionIds - List of conditions to alter
 * @param deactivated - <code>true</code> activates the conditions, <code>false</code> deactivates them.
 */
export function setConditionDeactivatedForActiveWorksheet(conditionIds: ConditionId[], deactivated: boolean) {
  // only when deactivating the last active condition, deactivate the
  // condition itself and clear deactivated by worksheet.
  const deactivatesLastActive = (conditionId: ConditionId) => {
    const deactivatedCount = getDeactivatedForWorksheets(getConditionById(getStore().getState(), conditionId)).length;
    return deactivated && deactivatedCount === numberOfWorksheets - 1;
  };
  const numberOfWorksheets = getWorksheets(getStore().getState()).length;
  const activationStateCommands = conditionIds.map((conditionId) =>
    deactivateConditionCommand(conditionId, deactivatesLastActive(conditionId)),
  );

  const toggleForWorksheetCommands = conditionIds.map((conditionId) =>
    deactivatesLastActive(conditionId) ?
      getDeactivateForWorksheetsCommand(conditionId, []) // clear deactivated
    : getSetDeactivateForActiveWorksheetCommand(conditionId, deactivated),
  );
  return getStore().dispatch(
    executeCommand(
      Command.COMPOSITE_COMMAND({
        commands: [...activationStateCommands, ...toggleForWorksheetCommands],
      }),
    ),
  );
}

function deactivateConditionCommand(conditionId: ConditionId, deactivated: boolean) {
  return Command.SET_CONDITION_SETTING({
    conditionId,
    settingName: ConditionSettingsKey.toPersistenceKey('CONDITION_DEACTIVATED'),
    settingValue: Boolean(deactivated).toString(),
  });
}

export function resetConditionCommand(condition: ConditionDto) {
  if (condition.type === 'QlCondition') {
    return Command.RESET_QL_CONDITION({ conditionId: condition.id });
  } else {
    return Command.SET_CONDITION({
      conditionId: condition.id,
      values: {
        conditionType: condition.type,
        valueItems: [],
      },
    });
  }
}

/**
 * Meant for bulk-operations like "deactivate all conditions" or "deactivate all conditions in group",
 * as explicitly deactivating a single conditions may ignore "mandatory".
 */
export function deactivatableConditionsBulk(conditionIds: ConditionId[]) {
  return conditionIds.filter(
    (conditionId) =>
      !isConditionMandatory(getStore().getState(), conditionId) &&
      !isConditionHidden(getStore().getState(), conditionId) &&
      getConditionDeactivated(getStore().getState(), conditionId) !== true,
  );
}

function getSetDeactivateForActiveWorksheetCommand(conditionId: ConditionId, deactivated: boolean) {
  const condition = getConditionById(getStore().getState(), conditionId);
  const worksheetsDeactivatingCondition = getDeactivatedForWorksheets(condition);
  const activeWorksheetId = getActiveWorksheetId(getStore().getState());
  if (deactivated) {
    if (worksheetsDeactivatingCondition.includes(activeWorksheetId)) {
      // no change: condition is already deactivated for active worksheet
      return getDeactivateForWorksheetsCommand(conditionId, worksheetsDeactivatingCondition);
    } else {
      return getDeactivateForWorksheetsCommand(
        conditionId,
        worksheetsDeactivatingCondition.concat([activeWorksheetId]),
      );
    }
  }
  return getDeactivateForWorksheetsCommand(
    conditionId,
    worksheetsDeactivatingCondition.filter((id) => id !== activeWorksheetId),
  );
}

function getDeactivateForWorksheetsCommand(conditionId: ConditionId, worksheets: WorksheetId[]) {
  return Command.SET_CONDITION_SETTING({
    conditionId,
    settingName: ConditionSettingsKey.toPersistenceKey('CONDITION_DEACTIVATED_FOR_WORKSHEETS'),
    settingValue: JSON.stringify(worksheets),
  });
}

export function canDeactivateAllConditions(conditionIds: ConditionId[]) {
  // If some of the non-mandatory conditions are activated, you can deactivate the lot
  return conditionIds
    .filter((conditionId) => !isConditionMandatory(getStore().getState(), conditionId))
    .some((conditionId) => !isConditionDeactivatedForActiveWorksheet(getStore().getState(), conditionId));
}

export function canDeactivateAllConditionsForActiveWorksheetOnly(conditionIds: ConditionId[]) {
  // If some of the non-mandatory conditions are activated, you can deactivate the lot
  return conditionIds
    .filter((conditionId) => !isConditionMandatory(getStore().getState(), conditionId))
    .some(
      (conditionId) =>
        getConditionDeactivated(getStore().getState(), conditionId) === 'INDETERMINATE' &&
        !isConditionDeactivatedForActiveWorksheet(getStore().getState(), conditionId),
    );
}

export function canActivateAllConditions(conditionIds: ConditionId[]) {
  // If some of the non-mandatory conditions are deactivated, you can activate the lot
  return conditionIds
    .filter((conditionId) => !isConditionMandatory(getStore().getState(), conditionId))
    .some((conditionId) => isConditionDeactivatedForActiveWorksheet(getStore().getState(), conditionId));
}

export function isDeactivatedIndeterminateForAny(conditionIds: ConditionId[]) {
  return conditionIds.some(
    (conditionId) => getDeactivated(getConditionById(getStore().getState(), conditionId)) === 'INDETERMINATE',
  );
}

export function canActivateAllConditionsForActiveWorksheet(conditionIds: ConditionId[]) {
  // If some of the non-mandatory conditions are deactivated, you can activate the lot
  return conditionIds
    .filter((conditionId) => !isConditionMandatory(getStore().getState(), conditionId))
    .some(
      (conditionId) =>
        getConditionDeactivated(getStore().getState(), conditionId) === 'INDETERMINATE' &&
        isConditionDeactivatedForActiveWorksheet(getStore().getState(), conditionId),
    );
}

/**
 * Meant for bulk-operations like "activate all conditions" or "activate all conditions in group",
 * as explicitly activating a single conditions may ignore "mandatory".
 */
export function activatableConditionsBulk(conditionIds: ConditionId[]) {
  return conditionIds
    .filter((conditionId) => !isConditionMandatory(getStore().getState(), conditionId))
    .filter((conditionId) => !isConditionHidden(getStore().getState(), conditionId))
    .filter((conditionId) => getConditionDeactivated(getStore().getState(), conditionId) !== false);
}

export function areAllConditionDeactivated(conditionIds: ConditionId[]) {
  // If all the conditions are deactivated, there should be only the action to activate
  return conditionIds.every((conditionId) => getConditionDeactivated(getStore().getState(), conditionId) === true);
}

export function isResettable(condition: ConditionDto) {
  return (
    !isConditionHidden(getStore().getState(), condition.id) && hasValuesSet(condition) && hasAffectedViews(condition.id)
  );
}

/**
 * Explicitly require passing in the `state` here, such that it becomes immediately obvious when
 * this function is called from non-workbook contexts.
 *
 * A variant that relies on getting the QL condition printed in the backend is in `condition-util.ts`.
 */
export function formatConditionValueAsText(
  state: RootStateOr<WorkbookState>,
  values?: WorkbookConditionValuesDto,
): string {
  if (!values) {
    return '';
  }

  switch (values.conditionType) {
    case 'QlCondition':
      return formatQlConditionValuesAsText(state, values);
    case 'PolygonCondition':
      return formatSpatialConditionValueAsText(values);
    default:
      return formatDefaultConditionValuesAsText(values);
  }
}
